import { Box, InputBaseComponentsPropsOverrides, Skeleton } from "@mui/material";
import { ReactNode, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LmsApiIntegration } from "./LmsApiIntegration";
import { VisualizerDownload } from "./VisualizerDownload";
import apiClient from "../../../helpers/api-client";
import { IntegrationSettingsActions } from "./IntegrationSettingsActions";
import { CoreCard, CoreMenuItem, CoreSelect } from "@verity/components/core";
import { SamlIntegration } from "./saml";
import VisualizerBanner from "../visualizer-banner";
import i18n from "../../../i18n";
import { ThemeWrapper } from "@verity/components";
import { PortalControllerContext } from "../../context";

type IntegrationOption = {
	name: string;
	component: ReactNode;
};

export function IntegrationSettings({ instituteId }: { instituteId: number }) {
	const navigate = useNavigate();
	const { pathname, search, state } = useLocation();

	const { portalContext } = useContext(PortalControllerContext);
	const { instituteUuid } = portalContext;

	const [integrationOptionList, setIntegrationOptionList] = useState<IntegrationOption[]>([]);
	const [lms, setLms] = useState<string | undefined>(portalContext.lms);
	const [isLoading, setIsLoading] = useState(true);
	const selectedOption = state?.selected;

	const updateSelectedIntegration = (optionName: string | undefined) => {
		if (!optionName) return;
		const searchParams = new URLSearchParams(search);
		navigate(
			{
				pathname,
				search: searchParams.toString(),
			},
			{
				state: { selected: optionName },
			},
		);
	};

	useEffect(() => {
		async function fetchData() {
			const info = instituteUuid ? await apiClient.institute.getAccessControlInfo(instituteUuid) : undefined;
			setLms(info?.platform ?? portalContext.lms ?? undefined);
		}

		setIsLoading(true);
		fetchData()
			.catch(console.error)
			.finally(() => setIsLoading(false));
	}, []);

	useEffect(() => {
		const list = [];
		if ((lms === "canvas" || lms === "desire2learn" || lms === "moodle") && instituteUuid) {
			list.push({
				name: "LMS API",
				component: <LmsApiIntegration instituteId={instituteId} instituteUuid={instituteUuid} lms={lms} />,
			});
		}
		if (lms === "canvas" || lms === "desire2learn" || lms === "moodle") {
			list.push({
				name: "LMS Visualizer",
				component: <VisualizerDownload instituteId={instituteId} lms={lms} />,
			});
		}
		if (instituteUuid) {
			list.push({
				name: "SSO - SAML",
				component: <SamlIntegration instituteUuid={instituteUuid} />,
			});
		}

		const initialOption = list.find((option) => option.name === selectedOption);
		const currentOption = initialOption ?? list?.[0];
		setIntegrationOptionList(list);
		if (!selectedOption && currentOption) {
			updateSelectedIntegration(currentOption.name);
		}
	}, [instituteId, instituteUuid, lms, selectedOption]);

	return (
		<ThemeWrapper
			scopedCssBaselineProps={{
				style: { width: "100%", minWidth: "250px", height: "100%", overflow: "auto" },
			}}
		>
			<div style={{ padding: "24px" }}>
				<Box sx={{ "&:not(:empty)": { marginBottom: "14px" } }}>
					<VisualizerBanner />
				</Box>
				{isLoading ? (
					<div>
						<Skeleton variant="text" height="22.5px" width="120px" />
						<Skeleton variant="rounded" height="32px" width="100px" />
						<IntegrationSettingsActions actions={[{}, {}]} loading />
						<CoreCard
							useHeader
							style={{ marginTop: "12px" }}
							title={<Skeleton variant="text" height="16px" width="200px" />}
							icon={<Skeleton variant="circular" height="16px" width="16px" />}
						>
							<Skeleton variant="rounded" height="200px" width="100%" />
						</CoreCard>
					</div>
				) : (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							width: "100%",
							height: "auto",
							minHeight: "100%",
							margin: "0 auto",
						}}
					>
						<div>{i18n.t("portal.components.settings.integrationSettingsTSX.selectIntegration")}</div>
						<CoreSelect
							value={selectedOption ?? ""}
							onChange={(e) => updateSelectedIntegration(e.target.value)}
							renderValue={(selected) => selected}
							label={i18n.t("portal.components.settings.integrationSettingsTSX.selectAriaLabel")}
							autoWidth
							slotProps={{
								input: {
									sx: {
										padding: "5px 9px",
									},
								} as InputBaseComponentsPropsOverrides,
							}}
							MenuProps={{
								anchorOrigin: {
									vertical: "bottom",
									horizontal: "left",
								},
								transformOrigin: {
									vertical: "top",
									horizontal: "left",
								},
							}}
							sx={{
								height: "32px",
								width: "fit-content",
								maxWidth: "140px",
							}}
						>
							{integrationOptionList.map((x) => (
								<CoreMenuItem value={x.name} key={x.name}>
									{x.name}
								</CoreMenuItem>
							))}
						</CoreSelect>
						{integrationOptionList.find((integration) => integration.name === selectedOption)?.component}
					</div>
				)}
			</div>
		</ThemeWrapper>
	);
}
